import { useMemo } from 'react';
import { ExpressionValue } from '@shared/models/project.model';
import {
  QuantityUnit,
  quantityUnitToMathJSUnit,
} from '@shared/models/unit.model';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { solve } from '@shared/helpers/expression-solving.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { mathJS } from '@shared/helpers/mathjs';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { tryCatch } from '@shared/helpers/utils.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { guards } from '@shared/helpers/typeguards.helpers';
import { isNumeric } from '@shared/helpers/math.helpers';
import { UNICODE } from '@shared/models/unicode.model';
import { GET_SBEF_LIFETIME_FN_NAME } from '@shared/helpers/mathjs/extentions/get-sbef-lifetime.helper';
import { getVariablesInExpression } from '@shared/helpers/mathjs';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { ElementPropertyName } from '@shared/models/element-property.model';

export type DisplayMode = 'preview' | 'editing' | 'inactive';

/**
 * Determines the current display mode based on component state
 * @param isExpressionPanelMine - Whether expression panel is open for this input
 * @param isFocused - Whether the input is currently focused
 * @param value - The resolved expression value
 * @returns The current display mode
 */
const getDisplayMode = (
  isExpressionPanelMine: boolean,
  isFocused: boolean,
): DisplayMode => {
  if (isExpressionPanelMine) return 'preview';
  if (isFocused) return 'editing';
  return 'inactive';
};

/**
 * Adjusts an expression value for percentage display
 * @param expressionValue - The {@link ExpressionValue} to format
 * @param unit - The unit (percentage values get multiplied by 100 for display)
 * @returns Display string with percentage adjustment if needed
 */
const adjustPercentValue = (
  expressionValue: ExpressionValue,
  unit: QuantityUnit | undefined,
): number => {
  if (unit !== '%') return expressionValue.resolved;

  const value = expressionValue.value ?? expressionValue.resolved;
  return mathJS.bignumber(value).mul(100).toNumber(); // fix floating point precision issues for display
};

const isError = (value: unknown): value is Error => value instanceof Error;

/**
 * True when every symbol in {@link expression} is a finite, non-zero number in
 * {@link variables}. A zero dependency is treated as missing (DEV-3343: client
 * scope can be 0 while the pipeline resolved number is the real amount).
 * Formulas with no symbols can always be solved here (DEV-3951).
 */
const canResolveExpressionFromVariables = (
  expression: string,
  variables: ExpressionVariables | undefined,
): boolean => {
  const names = getVariablesInExpression(expression);
  if (names.length === 0) return true;
  if (!variables) return false;
  return names.every((name) => {
    const value = variables[name];
    return guards.is.finiteNumber(value) && value !== 0;
  });
};

/**
 * True when {@link getSBEFLifetime} can read a user-set ancestor lifetime from
 * {@link EXPRESSION_VARIABLE_NAME.USER_DEFINED} (proposal overrides included).
 */
const hasPositiveUserDefinedLifetime = (
  variables: ExpressionVariables | undefined,
): boolean => {
  const userDefinedLifetime =
    variables?.[EXPRESSION_VARIABLE_NAME.USER_DEFINED]?.[
      ElementPropertyName.Lifetime
    ];
  return guards.is.number(userDefinedLifetime) && userDefinedLifetime > 0;
};

// Helper to get standard placeholder when not in expression panel mode
const getPlaceholder = (
  expressionValue: ExpressionValue | undefined | Error,
  localUnit: QuantityUnit | undefined,
  useDashFallback = false,
): string => {
  if (isError(expressionValue) || expressionValue === undefined) return '';
  if (useDashFallback && expressionValue.resolved === 0) {
    return UNICODE.DASH.EN_DASH;
  }
  return getDisplayNumber(adjustPercentValue(expressionValue, localUnit));
};

/**
 * Formats an expression and preserves the converted number for dependent displays.
 */
const getExpressionDisplayData = (
  expressionValue: ExpressionValue,
  localUnit: QuantityUnit | undefined,
  displayUnit: string | undefined,
) => {
  if (displayUnit) {
    const valueAsNumber = adjustPercentValue(expressionValue, localUnit);
    return {
      value: getDisplayNumber(valueAsNumber),
      valueAsNumber,
      unit: localUnit,
    };
  }
  return getDisplayValueData({
    value: expressionValue.resolved,
    unit: quantityUnitToMathJSUnit(localUnit),
  });
};

export type UseDisplayValuesReturn = {
  /** The value to display in the input field */
  value: number | undefined;
  /** The placeholder text to show when input is empty */
  placeholder: string;
  /**
   * Numeric placeholder value: display-adjusted when inactive/preview (animation),
   * raw resolved when editing (ArrowRight fill). Undefined when there is no meaningful fallback.
   */
  placeholderNumber?: number;
  /** The of value & placeholder. Can be different from localUnit if the input is in inactive mode */
  unit: string | undefined;
};

export type UseDisplayValuesProps = {
  /** Whether this expression panel instance is currently open */
  isExpressionPanelMine: boolean;
  /** Whether the input is currently focused */
  isFocused: boolean;
  /** The local expression value (could be Error) */
  localValue?: ExpressionValue | Error;
  /** The local expression value (could be Error) */
  originalValue?: ExpressionValue;
  /** The fallback expression value */
  fallbackValue?: ExpressionValue;
  /** The local unit state */
  localUnit?: QuantityUnit;
  /** When set, disables auto unit conversion in placeholders (UnitNumberInput will override the unit anyway) */
  displayUnit?: string;
  /** Expression variables for solving */
  variables?: ExpressionVariables;
  /** Solve options containing min/max constraints */
  solveOptions: { min?: number; max?: number };
};

/**
 * Hook that determines the display value and placeholder & accompanying unit for an ExpressionInput component.
 * Uses explicit mode determination with exhaustive switch cases for clarity.
 *
 * NOTE: This function now supports auto conversion of values & units for inactive & preview modes. Consumer MUST use the returned unit for display, as it may be different from the input unit.
 *
 * When `count` has an expression, display uses pipeline `count.resolved` (not client re-solve).
 * When there is no `count` but `fallbackCount` has an expression (inherit / parent-driven quantities),
 * the placeholder is solved from that expression with `variables` so it tracks proposal context — not stale `fallbackCount.resolved` from baseline.
 * `getSBEFLifetime` is re-solved when {@link EXPRESSION_VARIABLE_NAME.USER_DEFINED}.lifetime is set;
 * otherwise the pipeline `fallbackCount.resolved` is kept.
 *
 * @param props - {@link UseDisplayValuesProps} Configuration object containing all necessary dependencies
 * @returns result {@link UseDisplayValuesReturn} Object with computed value and placeholder strings
 */
export const useExpressionDisplayValues = (
  props: UseDisplayValuesProps,
): UseDisplayValuesReturn => {
  const {
    isExpressionPanelMine,
    isFocused,
    originalValue,
    localValue,
    fallbackValue,
    localUnit,
    displayUnit,
    variables,
    solveOptions,
  } = props;

  return useMemo((): UseDisplayValuesReturn => {
    const isFallbackDrivenDisplay =
      !isError(localValue) &&
      guards.is.undefined(localValue?.value) &&
      !guards.is.nonEmptyString(localValue?.expression);

    const placeholderValue = tryCatch(() => {
      if (isError(localValue)) return originalValue;

      // Explicit `count`: pipeline writes `resolved` (see updateVersionExpressionValues).
      // Client re-solve can under-resolve when dependencies only have fallbackCount — DEV-3343.
      // A cleared manual override leaves that same `resolved` on the old number. Re-solve
      // when the symbols in scope are real so the formula is what the row shows (DEV-3951).
      if (
        localValue &&
        guards.is.nonEmptyString(localValue.expression) &&
        localValue.value === undefined &&
        guards.is.defined(localValue.resolved)
      ) {
        if (
          canResolveExpressionFromVariables(localValue.expression, variables)
        ) {
          return solve(
            undefined,
            localValue.expression,
            variables,
            solveOptions,
          );
        }
        return localValue;
      }

      if (localValue && guards.is.nonEmptyString(localValue.expression)) {
        return solve(undefined, localValue.expression, variables, solveOptions);
      }

      // Inherit-only quantities: no `count`, display must re-solve `fallbackCount.expression`
      // (e.g. getParentValue) with current variables — not stale fallbackCount.resolved from baseline.
      //
      // Skip re-solve when the fallback expression is a pure numeric literal (e.g. the
      // default "0" for user-added `inheritFallback` properties — DEV-3336). The compute
      // pipeline writes the inherited value to `fallbackValue.resolved` via the inheritance
      // node; re-solving the literal "0" would discard that and show 0 instead of the
      // parent value.
      //
      // Also skip getSBEFLifetime unless user_defined.lifetime is in scope: the
      // helper reads ancestor `lifetime` via scope.get('lifetime'), but `variables`
      // here excludes the property's own name (`lifetime`) to avoid self-reference,
      // so a re-solve would collapse to building_lifetime. Proposal overrides cascade
      // on user_defined (a different key), so those can re-solve safely.
      const skipSbefLifetimeResolve =
        !!fallbackValue?.expression?.includes(GET_SBEF_LIFETIME_FN_NAME) &&
        !hasPositiveUserDefinedLifetime(variables);
      if (
        fallbackValue &&
        guards.is.nonEmptyString(fallbackValue.expression) &&
        !isNumeric(fallbackValue.expression) &&
        !skipSbefLifetimeResolve
      ) {
        return solve(
          undefined,
          fallbackValue.expression,
          variables,
          solveOptions,
        );
      }

      return fallbackValue;
    }, fallbackValue).value;

    // Raw resolved number behind the placeholder, in the typed (base) unit — the
    // same basis as `value`, so an ArrowRight fill edits like a committed value
    // (getDecimalDisplayNumber), never the pretty-formatted "7.85 × 10¹²" string.
    // The en-dash fallback (resolved 0 with no real fallback) has nothing to fill.
    const placeholderNumber =
      isFallbackDrivenDisplay && placeholderValue?.resolved === 0
        ? undefined
        : placeholderValue?.resolved;

    const mode = getDisplayMode(isExpressionPanelMine, isFocused);

    switch (mode) {
      case 'preview': {
        // Expression panel is open - show solved expression as placeholder
        const { value: previewDisplayData } = tryCatch(
          () => {
            const solvedValue =
              placeholderValue &&
              placeholderValue.value === undefined &&
              guards.is.defined(placeholderValue.resolved)
                ? placeholderValue
                : solve(
                    undefined,
                    placeholderValue?.expression,
                    variables,
                    solveOptions,
                  );
            // UnitNumberInput owns a custom display unit, while automatic units stay converted here.
            return getExpressionDisplayData(
              solvedValue,
              localUnit,
              displayUnit,
            );
          },
          {
            value: 'Could not solve formula',
            valueAsNumber: undefined,
            unit: localUnit,
          },
        );

        return {
          value: undefined,
          placeholder: previewDisplayData.value,
          placeholderNumber: previewDisplayData.valueAsNumber,
          unit: previewDisplayData.unit,
        };
      }

      case 'editing': {
        // Input is focused - show local input value with standard placeholder
        return {
          value: guards.is.error(localValue) ? undefined : localValue?.value,
          placeholder: getPlaceholder(
            placeholderValue,
            localUnit,
            isFallbackDrivenDisplay,
          ),
          placeholderNumber,
          unit: localUnit,
        };
      }

      case 'inactive': {
        // if there's an error, what to display?

        // if there's a value, display it. it should be autoconverted if possible, unit must be correct

        // if there's no value, display placeholder
        // in this case resolved value should be used, and autoconverted if possible, unit must be correct
        // value precedence is localValue => fallbackValue?

        // Skip auto unit conversion when displayUnit is set (UnitNumberInput overrides the unit anyway)
        const placeholderData = placeholderValue
          ? getExpressionDisplayData(placeholderValue, localUnit, displayUnit)
          : undefined;
        const placeholder =
          isFallbackDrivenDisplay && placeholderValue?.resolved === 0
            ? UNICODE.DASH.EN_DASH
            : (placeholderData?.value ?? '');
        const placeholderNumber =
          placeholder === UNICODE.DASH.EN_DASH
            ? undefined
            : placeholderData?.valueAsNumber;

        if (isError(localValue)) {
          return {
            value: undefined,
            placeholder,
            placeholderNumber,
            unit: placeholderData?.unit ?? localUnit,
          };
        }
        if (localValue?.value === undefined) {
          return {
            value: undefined,
            placeholder,
            placeholderNumber,
            unit: placeholderData?.unit ?? localUnit ?? '',
          };
        }
        return {
          value: localValue.value,
          placeholder: '',
          placeholderNumber: undefined,
          unit: localUnit,
        };
      }

      default: {
        // Exhaustiveness check - TypeScript will error if we miss a case
        throw assertUnreachable(mode);
      }
    }
  }, [
    isExpressionPanelMine,
    isFocused,
    originalValue,
    localValue,
    fallbackValue,
    localUnit,
    displayUnit,
    variables,
    solveOptions,
  ]);
};
